import type { Verification } from "@sitescene/types";

export interface VerifierCall {
	/** How the verifier reached it, for the wording shown beside it. */
	basis: "alerted" | "benign" | "blind";
	/** A taxonomy class id, or "none" when the verifier found nothing alertable. */
	classId: string;
}

/**
 * The class the verifier concluded the clip shows — which is not always the
 * class it checked. Mirrors the finding the live-apply service writes onto the
 * measurement (`PlausibilityLiveApplyService.composeFinding`), so the detail
 * page, the verdict default and the rewritten event summary agree:
 *
 * - Stage 1's own words named a look-alike, down-ranked without a footage
 *   review → that look-alike.
 * - The verifier alerted (alert now or escalate) → the class it checked.
 * - Otherwise → the independent blind pass's top class, "none" when it saw
 *   nothing alertable.
 *
 * Undefined when there is no call to report: a row that did not complete, or
 * one with no blind answer to fall back on.
 */
export function verifierCall(
	v: Pick<
		Verification,
		"benignResolution" | "blind" | "classId" | "decision" | "status"
	>,
): undefined | VerifierCall {
	if (v.status !== "completed") return undefined;
	if (v.benignResolution?.action === "downRanked") {
		return { basis: "benign", classId: v.benignResolution.benignClassId };
	}
	if ((v.decision === "alertNow" || v.decision === "escalate") && v.classId) {
		return { basis: "alerted", classId: v.classId };
	}
	const top = v.blind?.ranking?.topClassId;
	return top ? { basis: "blind", classId: top } : undefined;
}
